import { supabase } from '../../../core/lib/supabase';

export interface Prompt {
  id: string;
  title: string;
  prompt: string;
  workpill_id: string;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  workpills?: {
    id: string;
    name: string;
    description: string | null;
  } | null;
}

/**
 * Obtiene todos los prompts asociados a un workpill específico
 * @param workpillId - ID del workpill
 * @returns Array de prompts ordenados por fecha de creación (más recientes primero)
 */
export async function getPromptsByWorkpill(workpillId: string): Promise<Prompt[]> {
  if (!workpillId) {
    console.warn('⚠️ No workpillId provided to getPromptsByWorkpill');
    return [];
  }

  try {
    // Verificar que el usuario esté autenticado
    const { data: { user }, error: authError } = await supabase.auth.getUser();
    if (authError || !user) {
      console.error('❌ Error getting user:', authError);
      throw new Error('No authenticated user');
    }

    // Obtener prompts del workpill con información del workpill
    const { data, error } = await supabase
      .from('prompts')
      .select(`
        id,
        title,
        prompt,
        workpill_id,
        created_by,
        created_at,
        updated_at,
        workpills:workpill_id (
          id,
          name,
          description
        )
      `)
      .eq('workpill_id', workpillId)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('❌ Error fetching prompts:', error);
      throw error;
    }

    // Transformar los datos para manejar correctamente la relación workpills
    const prompts: Prompt[] = (data || []).map((item: any) => {
      // Supabase puede devolver workpills como array o como objeto único
      const workpill = Array.isArray(item.workpills) 
        ? item.workpills[0] 
        : item.workpills;

      return {
        id: item.id,
        title: item.title,
        prompt: item.prompt,
        workpill_id: item.workpill_id,
        created_by: item.created_by,
        created_at: item.created_at,
        updated_at: item.updated_at,
        workpills: workpill || null
      };
    });

    console.log(`✅ Fetched ${prompts.length} prompts for workpill ${workpillId}`);
    return prompts;
  } catch (error) {
    console.error('💥 Error in getPromptsByWorkpill:', error);
    throw error;
  }
}

/**
 * Obtiene todos los prompts de workpills asignados al usuario actual
 * @returns Array de prompts ordenados por fecha de creación (más recientes primero)
 */
export async function getPromptsForUser(): Promise<Prompt[]> {
  try {
    // Verificar que el usuario esté autenticado
    const { data: { user }, error: authError } = await supabase.auth.getUser();
    if (authError || !user) {
      console.error('❌ Error getting user:', authError);
      throw new Error('No authenticated user');
    }

    // Primero obtener los workpills asignados al usuario
    const { data: assignations, error: assignationError } = await supabase
      .from('workpill_assignations')
      .select('workpill_id')
      .eq('user_id', user.id);

    if (assignationError) {
      console.error('❌ Error fetching assignations:', assignationError);
      throw assignationError;
    }

    if (!assignations || assignations.length === 0) {
      console.log('⚠️ No workpills assigned to user');
      return [];
    }

    const workpillIds = assignations.map(a => a.workpill_id);

    // Obtener prompts con información del workpill
    const { data, error } = await supabase
      .from('prompts')
      .select(`
        id,
        title,
        prompt,
        workpill_id,
        created_by,
        created_at,
        updated_at,
        workpills:workpill_id (
          id,
          name,
          description
        )
      `)
      .in('workpill_id', workpillIds)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('❌ Error fetching prompts:', error);
      throw error;
    }

    // Transformar los datos para manejar correctamente la relación workpills
    const prompts: Prompt[] = (data || []).map((item: any) => {
      // Supabase puede devolver workpills como array o como objeto único
      const workpill = Array.isArray(item.workpills) 
        ? item.workpills[0] 
        : item.workpills;

      return {
        id: item.id,
        title: item.title,
        prompt: item.prompt,
        workpill_id: item.workpill_id,
        created_by: item.created_by,
        created_at: item.created_at,
        updated_at: item.updated_at,
        workpills: workpill || null
      };
    });

    console.log(`✅ Fetched ${prompts.length} prompts for user`);
    return prompts;
  } catch (error) {
    console.error('💥 Error in getPromptsForUser:', error);
    throw error;
  }
}



